import type { ProductInfo } from "@expert/contentful";
import type { JSX } from "react";

import { Button, Container, Flex, Stack, Text } from "@mantine/core";

import { useSalesContext } from "../../../../sales-context/SalesContext";
import { useProductOfferStore } from "../../../../stores";
import { EligibilityStatus, SaleProgress } from "../../../../types";
import { hasVerbatimToken, RichPhrase } from "../RichPhrase";
import { getSalesViewConfig } from "./SaleStatusMap";
import classes from "./SaleView.module.css";

interface SaleViewProps {
    status: EligibilityStatus | SaleProgress;
    planType?: "Home" | "Other";
    onPrimarySaleComplete?: () => void;
    productInfo?: ProductInfo;
}

export function SaleView({
    status,
    planType = "Home",
    onPrimarySaleComplete,
    productInfo,
}: SaleViewProps): JSX.Element {
    const { sessionId } = useSalesContext();
    const hasOtherProducts = useProductOfferStore((state) =>
        state.getOtherProducts(sessionId).some((p) => p.status === EligibilityStatus.Eligible),
    );

    const { statusHeader, statusBody, icon } = getSalesViewConfig(status);
    const successMessage =
        status === SaleProgress.Success && productInfo?.saleSuccessMessage ? productInfo.saleSuccessMessage : undefined;
    const hasMarkedSuccessMessage = !!successMessage && hasVerbatimToken(successMessage);

    return (
        <>
            <Flex
                className={status !== SaleProgress.Declined ? classes.statusContainer : classes.declinedContainer}
                p={14}
                data-testid="sale-finished-view"
            >
                <Stack gap={0}>
                    <Text fw={700} fz="sm" className={classes.statusHeader} data-testid="sale-view-status-header">
                        {statusHeader}
                    </Text>
                    <Text
                        // Only marked-up copy renders through RichPhrase, and only then does this need
                        // to be a div (RichPhrase emits block content). Every other partner's message
                        // stays on the plain-string path it has always used.
                        component={hasMarkedSuccessMessage ? "div" : "p"}
                        className={classes.statusBodyText}
                        fz="xs"
                        pt={4}
                        data-testid="sale-view-status-body"
                    >
                        {hasMarkedSuccessMessage ? (
                            <RichPhrase markdown={successMessage!} />
                        ) : (
                            (successMessage ?? statusBody(planType, hasOtherProducts))
                        )}
                    </Text>
                </Stack>

                <Container className={status !== SaleProgress.Declined ? classes.statusIcon : classes.declinedIcon}>
                    {icon}
                </Container>
            </Flex>
            <Flex className={classes.footerBox}>
                <Button
                    color={"white"}
                    fz="xs"
                    size="sm"
                    variant={"outline"}
                    data-testid="close-sales-view"
                    onClick={onPrimarySaleComplete}
                >
                    Close
                </Button>
            </Flex>
        </>
    );
}
